<template>
  <Layout>
    <div class="router-view footer-padding">
      <div class="max-w-md mx-auto">
        <div class="pl-0 pr-0 text-sm">
          <div class="px-4 mb-6">
            <p class="text-sm font-bold">
              {{ `Quote reference: ${$store.getters.getReference}` }}
            </p>
          </div>
          <DetailsOverview :is-confirmation="isConfirmation" />
          <div v-if="$store.getters.getProducts.length" class="pb-4">
            <ProductsOverview :is-confirmation="isConfirmation" />
          </div>
          <div v-if="isConfirmation && isPolicyEditable" class="px-6 py-4">
            <div class="flex items-center">
              <DrIcon svg="checkmark" width="24" height="24" class="text-green-500" />
              <span>Cancel at any time with 30 days notice</span>
            </div>
            <div class="flex items-center">
              <DrIcon svg="checkmark" width="24" height="24" class="text-green-500" />
              <span>Make changes without any fees</span>
            </div>
          </div>
          <Transition v-if="getSource !=='appear-here'" name="slide">
            <section v-if="!isPortal">
              <div class="py-4 px-6 border-b border-grey-200 mb-4">
                <ReferralCode />
              </div>
              <DrInputCheckbox
                v-if="shouldUrbanConsent"
                v-model="consent"
                class="mb-6"
                data-cy="urban-consent"
                data-label="I consent to allow my policy data to be shared with Urban."
              />
            </section>
          </Transition>
          <div class="py-4 px-6">
            <div class="mb-4">
              <div
                v-if="getDiscount.length || getFee || zeroValueCoupons.length"
                class="flex justify-between w-full mb-3"
              >
                <div>Monthly premium (including tax)</div>
                <div class="w-1/4 sm:w-1/5 text-right" data-cy="monthly-total-before-discount">
                  {{ getTotal | currency(2) }}
                </div>
              </div>
              <div v-for="(discount, i) in getDiscount" :key="`discount-${i}`" class="flex justify-between w-full mb-3">
                <div data-cy="coupon-description">{{ `${discount.description}` }}</div>
                <div class="w-1/4 sm:w-1/5 text-right" data-cy="discount-individual-total">
                  -{{ `${(discount.amount + discount.tax) / 100}` | currency(2) }}
                </div>
              </div>
              <div
                v-for="(couponId, i) in zeroValueCoupons"
                :key="`coupon-${i}`"
                class="flex justify-between w-full mb-3"
              >
                <div data-cy="coupon-description">{{ zeroValueCouponDescription(couponId) }}</div>
                <DrIcon class="text-blue-500 mt-1 mr-2" svg="checkmark-solid" />
              </div>
              <div v-if="getFee" class="flex justify-between w-full mb-3">
                <div data-cy="coupon-description">Our fee</div>
                <div class="w-1/4 sm:w-1/5 text-right">{{ getFee | currency(2) }}</div>
              </div>
              <div
                :class="{ 'border-t': getDiscount.length || getFee || zeroValueCoupons.length }"
                class="flex justify-between w-full pt-3  font-bold"
              >
                <div v-if="!isPortal"><template v-if="!getPaymentLumpSum">Monthly total</template><template v-else>Total</template> (including tax)</div>
                <div v-else>New <template v-if="!getPaymentLumpSum">monthly </template> (including tax)</div>
                <div class="w-1/4 sm:w-1/5 text-right" data-cy="monthly-total-after-discount">
                  {{ getMonthlyTotal | currency(2) }}
                </div>
              </div>
            </div>
            <div v-if="isConfirmation">
              <p class="text-2xs  text-center w-3/4 mx-auto mb-2">
                This service is provided by Digital Risks, authorised and regulated by the Financial Conduct Authority
                656459.
              </p>
              <p class="text-2xs  text-center w-3/4 mx-auto mb-0">
                By using this service you have agreed to our
                <a class="no-underline text-blue-500" href="https://docs.digitalrisks.co.uk/terms_of_business_agreement.pdf" target="_blank" rel="noopener noreferrer">
                  terms of business
                </a>
                and
                <a
                  class="no-underline text-blue-500"
                  href="https://www.digitalrisks.co.uk/privacy-notice"
                  target="_blank" rel="noopener noreferrer"
                >
                  privacy notice.
                </a>
              </p>
            </div>
            <div v-else class="text-2xs  text-center w-3/4 mx-auto">
              The premium above is an indication.<br>
              Click continue to finalise your quote.
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import Layout from '@/router/layouts/Main'
import DetailsOverview from '@/components/overview/DetailsOverview'
import ProductsOverview from '@/components/overview/ProductsOverview'
import ReferralCode from '@/components/overview/ReferralCode'

export default {
  components: {
    Layout,
    DetailsOverview,
    ProductsOverview,
    ReferralCode,
  },
  props: {
    isConfirmation: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters(['getTotal', 'getDiscount', 'getFee', 'getMonthlyTotal', 'getChargebeeEstimate', 'couponIds', 'shouldUrbanConsent', 'urbanConsent', 'getPaymentLumpSum', 'getSource']),
    configuredProducts () {
      return this.$store.getters.getProducts
    },
    coupon () {
      return this.$store.getters.getChargebeeEstimate
    },
    isPortal () {
      return !!this.$store.state.portal
    },
    zeroValueCoupons () {
      const discounts = this.getChargebeeEstimate.discounts
      const zeroValueCoupons = []
      this.couponIds.forEach((coupon) => {
        if (!discounts) zeroValueCoupons.push(coupon)
        if (discounts && !discounts.find(discount => discount.entity_id.toUpperCase() === coupon.toUpperCase())) zeroValueCoupons.push(coupon)
      })
      return zeroValueCoupons
    },
    isPolicyEditable () {
      return this.getSource !== 'appear-here'
    },
    consent: {
      get () {
        return this.urbanConsent
      },
      set (value) {
        this.$store.commit('setUrbanConsent', value)
      },
    },
  },
  created () {
    this.gtmDataLayer()
    if (new Date(this.$store.state.contact.effectiveDate).getTime() < Date.now()) {
      this.updateEffectiveDate(new Date(Date.now()))
    }
  },
  methods: {
    ...mapMutations({
      updateEffectiveDate: 'updateEffectiveDate',
    }),

    /**
     * The Chargebee estimate doesn't contain the coupon details
     * if the coupon doesn't apply any discount (for example
     * if a previous coupon has already taken the total to 0).
     *
     * So, we have to manually provide the description here.
     */
    zeroValueCouponDescription (couponId) {
      const couponIdToDescription = {
        'URBAN10': 'Urban 10% Off Forever',
      }

      return couponIdToDescription[couponId] || couponId
    },

    applyItp: val => val * 1.12,

    gtmDataLayer () {
      // send tracking params to Google Tag manager
      const payload = {
        event: 'quoteOverview',
        rates: this.$store.getters.getSelectedRatesIds,
      }
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(payload)
    },
  },
}
</script>
